import { ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useDroppable } from "@dnd-kit/core";
import { FolderContentsResponse } from "@repo/api/folder";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { paths } from "@/App";
import { cn } from "@/lib/utils";
import FolderActionsMenu from "./FolderActionsMenu";
import { FolderDropData, folderDropId, VideosDragData } from "./videos-dnd";

type Props = {
  teamExternalId: string;
  contents: FolderContentsResponse;
};

export default function Breadcrumb({ teamExternalId, contents }: Props) {
  const navigate = useNavigate();

  return (
    <nav aria-label="Breadcrumb" className="mr-1 min-w-0 overflow-x-auto">
      <ol className="flex flex-nowrap items-center gap-1">
        <li className="flex shrink-0 items-center gap-1">
          <BreadcrumbDropLink
            to={paths.videos(teamExternalId)}
            folderExternalId={null}
            className="-ml-1.5 rounded-sm px-1.5 py-0.5 text-sm font-medium text-text-secondary transition-colors hover:border-border hover:bg-grey-100 hover:text-text-primary"
          >
            Videos
          </BreadcrumbDropLink>
        </li>
        {contents.breadcrumb.map((crumb, index) => {
          const isLast = index === contents.breadcrumb.length - 1;
          return (
            <li
              key={crumb.externalId}
              className="flex shrink-0 items-center gap-1"
            >
              <IconChevronRight
                size={16}
                className="shrink-0 text-text-secondary"
              />
              {isLast ? (
                <FolderActionsMenu
                  folder={crumb}
                  teamExternalId={teamExternalId}
                  folderDepth={index + 1}
                  align="start"
                  anchorClassName="min-w-0"
                  onDeleteSuccess={() =>
                    navigate(
                      crumb.parentFolderExternalId
                        ? paths.videosFolder(
                            teamExternalId,
                            crumb.parentFolderExternalId,
                          )
                        : paths.videos(teamExternalId),
                    )
                  }
                >
                  <button
                    type="button"
                    aria-current="page"
                    className="flex min-w-0 cursor-pointer items-center gap-1 rounded-sm px-1.5 py-0.5 pr-1.5 text-sm font-medium transition-colors hover:bg-grey-100 hover:text-text-primary"
                  >
                    <span className="mr-0.5 whitespace-nowrap">
                      {crumb.name}
                    </span>
                    <IconChevronDown
                      size={16}
                      className="shrink-0 text-text-secondary"
                    />
                  </button>
                </FolderActionsMenu>
              ) : (
                <BreadcrumbDropLink
                  to={paths.videosFolder(teamExternalId, crumb.externalId)}
                  folderExternalId={crumb.externalId}
                  className="rounded-sm px-1.5 py-0.5 text-sm font-medium whitespace-nowrap text-text-secondary transition-colors hover:bg-grey-100 hover:text-text-primary"
                >
                  {crumb.name}
                </BreadcrumbDropLink>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function BreadcrumbDropLink({
  to,
  folderExternalId,
  className,
  children,
}: {
  to: string;
  folderExternalId: string | null;
  className?: string;
  children: ReactNode;
}) {
  const { setNodeRef, isOver, active } = useDroppable({
    id: folderDropId(folderExternalId),
    data: { folderExternalId } satisfies FolderDropData,
  });

  const activeDragData = active?.data.current as VideosDragData | undefined;
  const isValidDropTarget =
    !!activeDragData &&
    !(
      activeDragData.type === "folder" &&
      activeDragData.folder.externalId === folderExternalId
    );

  return (
    <Link
      ref={setNodeRef}
      to={to}
      className={cn(
        className,
        !!active && "pointer-events-none",
        isOver &&
          isValidDropTarget &&
          "bg-tertiary-100 text-tertiary-400 hover:bg-tertiary-100",
      )}
    >
      {children}
    </Link>
  );
}
